import {
  ApiError,
  type BillingMachineAttachmentResource,
  type BillingMachineResource,
  BillingMachinesController_findOne_v1,
  type OutletResource,
  OutletsController_list_v1,
  type UserResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconArrowLeft, IconCircleCheck } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isNotFound } from '@/api/failure';
import { useSession } from '@/auth/session';
import { formatInstant } from '@/lib/instant';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { useActorNames } from '../catalogue/actor-names';
import { isRetired } from '../outlets/outlet-kinds';
import { billingMachineProductCodesPath, paths } from '../paths';
import { formatBusinessDate } from '../tickets/business-date';
import { AttachMachineDialog } from './AttachMachineDialog';
import { RenameMachineDialog } from './RenameMachineDialog';

/**
 * One billing machine and every outlet it has sold for (#228).
 *
 * The attachment history is the point of this screen: an uploaded bill is
 * booked to the outlet the machine was attached to **on the bill's date**, so
 * "where did Tuesday's slide takings go" is answered by reading down this table,
 * not by the machine's outlet today.
 *
 * Moving the machine is owner-only on the API and hidden from a manager;
 * renaming it is open to both (#341), so it is not gated here. A `404`
 * reads as "not found" — for a machine that does not exist and one outside the
 * caller's reach alike, which the API makes indistinguishable on purpose.
 */

type Loaded<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly value: T }
  | { readonly kind: 'failed'; readonly error: unknown };

const COLUMNS = 3;
const NO_OUTLETS: readonly OutletResource[] = [];

const backLink =
  'inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30';

export function BillingMachine() {
  const client = useApiClient();
  const { id } = useParams<{ id: string }>();
  const { session } = useSession();

  // Courtesy, not security; the API refuses a manager's move regardless.
  const mayMove = session?.user.role === 'OWNER';

  const [state, setState] = useState<Loaded<BillingMachineResource>>({ kind: 'loading' });
  const [outlets, setOutlets] = useState<readonly OutletResource[]>(NO_OUTLETS);
  const [moving, setMoving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [justMoved, setJustMoved] = useState(false);

  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  const actors = useActorNames();

  useEffect(() => {
    if (id === undefined) return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(BillingMachinesController_findOne_v1, { path: { id }, signal: controller.signal })
      .then((machine) => {
        if (live) setState({ kind: 'loaded', value: machine });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, id, reloads]);

  // Only the move dialog needs the outlets, so only an owner reads them. A
  // failed read leaves the picker empty rather than failing the record.
  useEffect(() => {
    if (!mayMove) return;

    const controller = new AbortController();
    let live = true;

    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((rows) => {
        if (live) setOutlets(rows);
      })
      .catch(() => {
        if (live) setOutlets(NO_OUTLETS);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, mayMove]);

  const openOutlets = useMemo(() => outlets.filter((outlet) => !isRetired(outlet)), [outlets]);

  if (id === undefined) return <Navigate to={paths.billingMachines} replace />;

  const machine = state.kind === 'loaded' ? state.value : null;

  // A machine that does not exist, one outside reach, and an id that is not a
  // UUID are one answer: a stale link or a hand-typed URL.
  const missing =
    state.kind === 'failed' &&
    (isNotFound(state.error) ||
      (state.error instanceof ApiError && state.error.code === 'VALIDATION_FAILED'));

  const current = machine?.currentAttachment ?? null;
  const attachments = machine?.attachments ?? [];

  return (
    <Screen
      title="Billing machine"
      description="One billing machine and every outlet it has sold for. A bill in its export is booked to the outlet the machine was attached to on the bill's own date, so a move never shifts takings already imported."
      action={
        machine === null ? undefined : (
          <div className="flex gap-2">
            <Button
              variant={mayMove ? 'outline' : 'default'}
              nativeButton={false}
              render={<Link to={billingMachineProductCodesPath(machine.id)} />}
            >
              Product codes
            </Button>
            <Button variant="outline" onClick={() => setRenaming(true)}>
              Rename
            </Button>
            {mayMove && <Button onClick={() => setMoving(true)}>Move machine</Button>}
          </div>
        )
      }
      aside={
        <>
          {machine !== null && (
            <>
              <ScreenFact label="Machine ID">
                <span className="font-mono numeric">{machine.machineCode}</span>
              </ScreenFact>
              <ScreenFact label="Name">{machine.name}</ScreenFact>
              <ScreenFact label="Sells for">
                {current === null
                  ? 'Not attached yet'
                  : (current.outletName ?? 'Attached to nothing')}
              </ScreenFact>
            </>
          )}

          <Link to={paths.billingMachines} className={backLink}>
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the machines
          </Link>
        </>
      }
    >
      {justMoved && (
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>
            The move has been recorded. Bills dated from{' '}
            {current === null ? 'that day' : formatBusinessDate(current.effectiveFrom)} are booked
            to {current?.outletName ?? 'no outlet'}.
          </AlertDescription>
        </Alert>
      )}

      {missing ? (
        <div className="mx-auto max-w-md space-y-3 py-8 text-center">
          <EmptyState
            title="Machine not found"
            description="There is no billing machine at this address. The link may be out of date — go back to the machines and open it from the list."
          />
        </div>
      ) : (
        <ScreenTable label="Attachment history">
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">From</TableHead>
              <TableHead>Outlet</TableHead>
              <TableHead className="w-80">Recorded</TableHead>
            </TableRow>
          </TableHeader>

          {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} rows={3} />}

          {state.kind === 'failed' && (
            <TableNotice columns={COLUMNS}>
              <LoadFailed
                error={state.error}
                onRetry={reload}
                title="This machine could not be loaded"
              />
            </TableNotice>
          )}

          {machine !== null &&
            (attachments.length === 0 ? (
              <TableNotice columns={COLUMNS}>
                <EmptyState
                  title="No attachments recorded"
                  description="This machine has never been attached to an outlet, so no bill from it can be booked yet."
                />
              </TableNotice>
            ) : (
              <TableBody>
                {attachments.map((attachment, index) => (
                  <AttachmentRow
                    key={attachment.id}
                    attachment={attachment}
                    // Newest first, and no attachment may be dated after today,
                    // so the first row is the one `currentAttachment` names.
                    inForce={index === 0 && current !== null}
                    actor={actors.get(attachment.recordedByActorId)}
                  />
                ))}
              </TableBody>
            ))}
        </ScreenTable>
      )}

      <RenameMachineDialog
        machine={renaming ? machine : null}
        onClose={() => setRenaming(false)}
        onRenamed={(renamed) => {
          setRenaming(false);
          // The answer is the machine as it now stands: authoritative.
          setState({ kind: 'loaded', value: renamed });
        }}
      />

      <AttachMachineDialog
        machine={moving ? machine : null}
        outlets={openOutlets}
        onClose={() => setMoving(false)}
        onAttached={(moved) => {
          setMoving(false);
          setJustMoved(true);
          // The answer is the machine with its new history: authoritative.
          setState({ kind: 'loaded', value: moved });
        }}
      />
    </Screen>
  );
}

function AttachmentRow({
  attachment,
  inForce,
  actor,
}: {
  attachment: BillingMachineAttachmentResource;
  inForce: boolean;
  actor: UserResource | undefined;
}) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-foreground numeric">
        {formatBusinessDate(attachment.effectiveFrom)}
        {inForce && (
          <>
            {' '}
            <Badge variant="outline">In force</Badge>
          </>
        )}
      </TableCell>
      <TableCell>
        {attachment.outletName ?? (
          <span className="text-muted-foreground">Attached to nothing</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        <span className="numeric">{formatInstant(attachment.recordedAt)}</span>
        {' · '}
        {actor === undefined ? (
          // A manager cannot read the staff list, and an actor need not be a
          // person; the id is what somebody quotes when they ask.
          <span className="font-mono text-xs numeric">{attachment.recordedByActorId}</span>
        ) : (
          <span className="text-foreground">{actor.displayName}</span>
        )}
      </TableCell>
    </TableRow>
  );
}
